/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable camelcase */
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { CalendarIcon } from '@radix-ui/react-icons';
import dayjs from 'dayjs';
import { X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Matcher } from 'react-day-picker';

const generateYearOptions = (start: number, end: number) => {
  const years = [];
  for (let year = start; year <= end; year++) {
    years.push(year?.toString());
  }
  return years;
};

const months = [
  { name: 'January', value: 0 },
  { name: 'February', value: 1 },
  { name: 'March', value: 2 },
  { name: 'April', value: 3 },
  { name: 'May', value: 4 },
  { name: 'June', value: 5 },
  { name: 'July', value: 6 },
  { name: 'August', value: 7 },
  { name: 'September', value: 8 },
  { name: 'October', value: 9 },
  { name: 'November', value: 10 },
  { name: 'December', value: 11 },
];

interface DateProp {
  placeholder?: string;
  onChange?: (date?: Date) => void;
  selectedDate?: Date;
  id?: string;
  onClear?: () => void;
  fromYear?: number;
  toYear?: number;
  classNames?: string;
  disabled?: boolean;
  textColor?: string;
  clearIcon?: boolean;
  format?: string;
  showCalanderIcon?: boolean;
  error?: string;
  requiredItem?: boolean;
  dateDisabled?: Matcher;
}

// const closeDropdown = () => {
//   document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
// };

export function DatePicker({
  placeholder = 'From date',
  onChange,
  id,
  onClear,
  fromYear = dayjs().year() - 100,
  toYear = dayjs().year() + 100,
  classNames,
  selectedDate,
  disabled,
  textColor,
  error,
  requiredItem,
  clearIcon = true,
  format = 'MMMM DD, YYYY hh:mm A',
  showCalanderIcon = true,
  dateDisabled,
}: DateProp) {
  const [open, setOpen] = useState(false);
  const years = useMemo(() => generateYearOptions(fromYear, toYear), [fromYear, toYear]);
  const [date, setDate] = useState<Date | undefined>(selectedDate);
  const [month, setMonth] = useState<string>(
    String(selectedDate ? dayjs(selectedDate).month() : dayjs().month()),
  );
  const [year, setYear] = useState<string>(
    String(selectedDate ? dayjs(selectedDate).year() : dayjs().year()),
  );

  useEffect(() => {
    if (selectedDate) {
      setDate(selectedDate);
      setMonth(String(dayjs(selectedDate).month()));
      setYear(String(dayjs(selectedDate).year()));
    }
  }, [selectedDate]);

  const clearDate = () => {
    setDate(undefined);
    setMonth(String(dayjs().month()));
    setYear(String(dayjs().year()));
    onClear?.();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className='relative'>
          <label
            className={cn(
              date
                ? 'font-normal text-xs absolute top-[10px] left-[11px] text-white'
                : 'font-normal text-[15px] absolute top-[20px] left-[11px] text-white',
              'transition-all duration-200',
              disabled ? 'cursor-default' : 'cursor-pointer',
            )}
          >
            {placeholder}
            {requiredItem && <span className='text-[#DC1D00]'>*</span>}
          </label>
          <Button
            disabled={disabled}
            id='date-pick'
            variant={'outline'}
            className={cn(
              'w-full border bg-transparent border-[#e2e8f0] hover:bg-transparent justify-start text-left font-normal h-[61px] transition-all duration-200 p-[10px]',
              !date && 'text-muted-foreground items-center',
              date && 'items-end',
              textColor,
              classNames,
              error ? 'border-red' : '',
            )}
            type='button'
          >
            <div className='flex items-center text-white'>
              {date ? dayjs(date).format(format) : ''}
            </div>
            {showCalanderIcon && (
              <CalendarIcon className='mr-2 h-6 w-6 absolute right-1 top-1/3 text-[#9e9e9f]' />
            )}{' '}
          </Button>
          {clearIcon && date && !disabled && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                clearDate();
              }}
              variant='ghost'
              size='icon'
              id={id ?? 'date-clear'}
              className='absolute right-10 z-10 top-[20px]'
              type='button'
            >
              <X className='opacity-70 text-white' />
            </Button>
          )}
          {error && (
            <p
              className='text-xs text-destructive absolute top-[63px] '
              id='phone-input-error'
              aria-live='polite'
            >
              {error}
            </p>
          )}
        </div>
      </PopoverTrigger>
      {!disabled && (
        <PopoverContent
          className='w-auto p-0 bg-[#151524] pointer-events-auto'
          align='start'
          side='bottom'
        >
          <div
            data-testid='date-picker-content'
            className='flex items-center gap-2 w-full p-3 pb-0'
          >
            <Select
              onValueChange={(e) => {
                setYear(e);
              }}
              value={year}
            >
              <SelectTrigger>
                <SelectValue placeholder='Year' className='flex-1' />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem
                    key={year}
                    value={year}
                    className={cn(String(dayjs().year()) === year && 'bg-gray-100')}
                  >
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              onValueChange={(e) => {
                setMonth(e);
              }}
              value={month}
            >
              <SelectTrigger>
                <SelectValue placeholder='Month' className='flex-1' />
              </SelectTrigger>
              <SelectContent>
                {months.map((month) => (
                  <SelectItem
                    className={cn(
                      String(dayjs().month()) === month.value?.toString() && 'bg-gray-100',
                    )}
                    key={month.value?.toString()}
                    value={month.value?.toString()}
                  >
                    {month.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Calendar
            month={new Date(Number(year), Number(month))}
            onMonthChange={(e) => {
              setMonth(String(e.getMonth()));
              setYear(String(e.getFullYear()));
            }}
            disabled={dateDisabled}
            className='bg-[#151524] pointer-events-auto'
            mode='single'
            selected={date}
            onSelect={(e) => {
              if (e) {
                setDate(e);
                // closeDropdown();
                setOpen(false);
                onChange?.(e);
              }
            }}
          />
        </PopoverContent>
      )}
    </Popover>
  );
}
